import { useEffect, useState } from 'react';
import { finApi } from '../../api/finance.js';

export default function Reports() {
  const [tab, setTab] = useState('pl');
  return (
    <section>
      <header className="page-header"><h1>Financial Reports</h1></header>
      <div className="tabs">
        <button className={tab === 'pl' ? 'tab active' : 'tab'} onClick={() => setTab('pl')}>P&amp;L</button>
        <button className={tab === 'bs' ? 'tab active' : 'tab'} onClick={() => setTab('bs')}>Balance Sheet</button>
        <button className={tab === 'cf' ? 'tab active' : 'tab'} onClick={() => setTab('cf')}>Cash Flow</button>
      </div>
      {tab === 'pl' && <ProfitLoss />}
      {tab === 'bs' && <BalanceSheet />}
      {tab === 'cf' && <CashFlow />}
    </section>
  );
}

function ProfitLoss() {
  const today = new Date().toISOString().slice(0, 10);
  const yearStart = new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10);
  const [from, setFrom] = useState(yearStart);
  const [to, setTo] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    finApi.profitLoss(from, to).then(setData).finally(() => setLoading(false));
  }, [from, to]);

  return (
    <div className="card">
      <div className="grid-form">
        <label>From<input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label>To<input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
      </div>
      {loading && <div className="muted">Loading…</div>}
      {data && (
        <>
          <h3>Revenue</h3>
          <ReportTable groups={data.revenue} subtotalLabel="Total Revenue" subtotal={data.totalRevenue} />
          <h3>Expenses</h3>
          <ReportTable groups={data.expenses} subtotalLabel="Total Expenses" subtotal={data.totalExpenses} />
          <div className="report-net">
            Net Income: <strong className={data.netIncome >= 0 ? 'positive' : 'negative'}>{data.netIncome.toFixed(2)}</strong>
          </div>
        </>
      )}
    </div>
  );
}

function BalanceSheet() {
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);

  useEffect(() => { finApi.balanceSheet(asOf).then(setData); }, [asOf]);

  return (
    <div className="card">
      <div className="grid-form">
        <label>As of<input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} /></label>
      </div>
      {data && (
        <>
          <h3>Assets</h3>
          <ReportTable groups={data.assets} subtotalLabel="Total Assets" subtotal={data.totalAssets} />
          <h3>Liabilities</h3>
          <ReportTable groups={data.liabilities} subtotalLabel="Total Liabilities" subtotal={data.totalLiabilities} />
          <h3>Equity</h3>
          <ReportTable groups={data.equity} subtotalLabel="Equity (carried)" subtotal={data.totalEquity - data.currentEarnings} />
          <div className="report-net">
            Current Earnings: <strong>{data.currentEarnings.toFixed(2)}</strong><br />
            Total Equity: <strong>{data.totalEquity.toFixed(2)}</strong><br />
            Liabilities + Equity: <strong>{data.totalLiabilitiesAndEquity.toFixed(2)}</strong> {data.inBalance ? '✓' : '⚠ out of balance'}
          </div>
        </>
      )}
    </div>
  );
}

function CashFlow() {
  const [data, setData] = useState(null);
  useEffect(() => { finApi.cashFlow().then(setData); }, []);
  if (!data) return <div className="muted">Loading…</div>;
  const buckets = ['0-30', '31-60', '61-90'];
  const max = Math.max(1, ...buckets.flatMap((b) => [data.projection[b].inflow, data.projection[b].outflow]));
  return (
    <div className="card">
      <h3>Cash Flow Projection</h3>
      <p>Cash on hand: <strong>{data.cashOnHand.toFixed(2)}</strong></p>
      <table className="data-table">
        <thead><tr><th>Window (days)</th><th>Inflows</th><th>Outflows</th><th>Net</th></tr></thead>
        <tbody>
          {buckets.map((b) => (
            <tr key={b}>
              <td>{b}</td>
              <td>{data.projection[b].inflow.toFixed(2)}</td>
              <td>{data.projection[b].outflow.toFixed(2)}</td>
              <td className={data.projection[b].net >= 0 ? 'positive' : 'negative'}>{data.projection[b].net.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="cashflow-bars">
        {buckets.map((b) => (
          <div key={b} className="cf-row">
            <div className="cf-label">{b}d</div>
            <div className="cf-bars">
              <div className="cf-bar bg-inflow" style={{ width: `${(data.projection[b].inflow / max) * 100}%` }} title={`In: ${data.projection[b].inflow.toFixed(2)}`} />
              <div className="cf-bar bg-outflow" style={{ width: `${(data.projection[b].outflow / max) * 100}%` }} title={`Out: ${data.projection[b].outflow.toFixed(2)}`} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReportTable({ groups, subtotalLabel, subtotal }) {
  return (
    <table className="data-table report-table">
      <tbody>
        {groups.map((g) => (
          <ReportGroup key={g.category} group={g} />
        ))}
        <tr className="report-subtotal"><td><strong>{subtotalLabel}</strong></td><td><strong>{Number(subtotal).toFixed(2)}</strong></td></tr>
      </tbody>
    </table>
  );
}

function ReportGroup({ group }) {
  return (
    <>
      <tr className="report-group"><td colSpan={2}>{group.category}</td></tr>
      {group.accounts.map((a) => (
        <tr key={a.accountId}>
          <td className="indent"><span className="muted small">{a.code}</span> {a.name}</td>
          <td>{a.balance.toFixed(2)}</td>
        </tr>
      ))}
      <tr className="report-group-sub"><td>Subtotal</td><td>{group.subtotal.toFixed(2)}</td></tr>
    </>
  );
}
